"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelDrawableMarker = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const mobx_1 = require("mobx");
const id_1 = require("../../../id");
const vector_1 = require("../../../math/vector");
const unit_1 = require("../../../unit");
const drawable_1 = require("./drawable");
const label_drawable_1 = require("./label.drawable");
const marker_offset_drawable_1 = require("./marker.offset.drawable");
class CollectionModelDrawableMarker extends drawable_1.MoveableDrawable {
    static createWithLabel(drawableState, markerPoint, labelPoint, id = id_1.IkeId.generate(), type = core_api_1.MarkerType.NONE, insight) {
        const labelId = `${id}:label`;
        const marker = new CollectionModelDrawableMarker(drawableState, id, markerPoint.y, labelId, type, insight);
        const label = new label_drawable_1.CollectionModelDrawableLabel(drawableState, labelId, labelPoint.x, labelPoint.y, marker.id, undefined, undefined);
        return { marker, label };
    }
    constructor(collectionModelDrawableState, drawableId, y, labelId, type = core_api_1.MarkerType.NONE, insight, pair, offset = 0) {
        super(collectionModelDrawableState, drawableId);
        this.y = y;
        this._labelId = labelId;
        this.type = type;
        this.insight = insight;
        this._pair = pair;
        this._offset = offset;
    }
    get x() {
        // Markers must always be in the center of the image
        if (this.drawableState.capture != null) {
            return this.drawableState.capture.resolution.width / 2;
        }
        return 0;
    }
    /**
     * Move the marker to this location
     *
     * **Markers can only be moved vertically**
     *
     * @param x coordinates in pixels
     * @param y coordinates in pixels
     */
    move(x, y) {
        const bounds = this.getPointInBounds(this.x, y);
        this.y = bounds.y;
    }
    moveNoBounds(x, y) {
        this.y = y;
    }
    get label() {
        return this.drawableState.getDrawable(this._labelId);
    }
    getLabelConnectionPoint(options) {
        return {
            x: this.label.isOnLeftSide()
                ? this.x - options.draw.state.markerOffsetWidth // Left
                : this.x + options.draw.state.markerOffsetWidth, // Right
            y: this.y
        };
    }
    get offset() {
        return this._offset;
    }
    set offset(offset) {
        if (!CollectionModelDrawableMarker.isBase(this)) {
            throw new Error('Cannot set offset on non base marker');
        }
        if (offset < 0) {
            throw new Error(`Offset cannot be a negative number; got: ${offset}`);
        }
        const baseOffset = this.drawableState.baseOffset;
        if (offset === 0) {
            // Remove old base offset marker
            baseOffset === null || baseOffset === void 0 ? void 0 : baseOffset.remove();
            this._offset = 0;
            return;
        }
        this._offset = offset;
        // Create new base offset marker if it doesn't exist
        if (baseOffset == null) {
            marker_offset_drawable_1.CollectionModelDrawableMarkerOffset.createWithLabel(this.drawableState);
        }
    }
    /**
     * Pair links two markers together -- it is used by when linked to vertical separation fields.
     */
    get pair() {
        return this._pair;
    }
    set pair(value) {
        this._pair = value;
    }
    get isInsightMeasurement() {
        var _a;
        // True when insight state exists and is not applied.
        // False when insight state doesn't exist, or it exists but is applied.
        return this.insight ? !((_a = this.insight) === null || _a === void 0 ? void 0 : _a.applied) : false;
    }
    isApprovableInsightDrawable() {
        return true;
    }
    get isVisible() {
        var _a;
        if (this.isInsightMeasurement) {
            // Show applied insight drawables
            if ((_a = this.insight) === null || _a === void 0 ? void 0 : _a.applied) {
                return true;
            }
            // If the image already has a base, then hide unapplied insight base/offset markers
            if (this.collectionHasBase) {
                return !this.isInsightBaseOffsetDetectionClass;
            }
        }
        return true;
    }
    /**
     * Shortcut to check if the insight detection class is a base offset marker.
     */
    get isInsightBaseOffsetDetectionClass() {
        var _a;
        if (!((_a = this.insight) === null || _a === void 0 ? void 0 : _a.detection_class)) {
            return false;
        }
        return (0, drawable_1.isInsightOffsetDetectionClass)(this.insight.detection_class);
    }
    approveInsightFlag() {
        if (!this.insight || this.insight.applied) {
            return;
        }
        this.insight.applied = true;
        // If approving a base offset detection, set the offset value.
        if (this.isInsightBaseOffsetDetectionClass) {
            if (this.drawableState.base) {
                throw new Error('Attempted to approve a base offset but a base already exists');
            }
            let offsetValue = 0;
            switch (this.insight.detection_class) {
                case drawable_1.InsightMarkerDetectionClass.NineFeetMarker:
                    offsetValue = 9;
                    break;
                case drawable_1.InsightMarkerDetectionClass.SixFeetMarker:
                    offsetValue = 6;
                    break;
                case drawable_1.InsightMarkerDetectionClass.ThreeFeetMarker:
                    offsetValue = 3;
                    break;
                default:
                    break;
            }
            this.type = core_api_1.MarkerType.BASE;
            this.offset = unit_1.IkeUnit.convert(offsetValue, unit_1.IkeUnit.METER, unit_1.IkeUnit.FEET, core_api_1.IkeUnitType.DISTANCE);
        }
    }
    static isMarker(t) {
        return t instanceof CollectionModelDrawableMarker;
    }
    static isBase(t) {
        return CollectionModelDrawableMarker.isMarker(t) && t.type === core_api_1.MarkerType.BASE;
    }
    /** Is this marker just a standard marker */
    static isNone(t) {
        return CollectionModelDrawableMarker.isMarker(t) && t.type === core_api_1.MarkerType.NONE;
    }
    // TODO: Rename this to something easier to understand, like "offset" or "height"
    get value() {
        if (this.type === core_api_1.MarkerType.BASE) {
            return this.offset || 0;
        }
        if (this.type === core_api_1.MarkerType.BASE_OFFSET) {
            return 0;
        }
        return this.calculateValue();
    }
    remove() {
        // Remove this marker's label too, unless it has already been removed
        if (this.drawableState.hasDrawable(this._labelId)) {
            this.label.remove();
        }
        super.remove();
    }
    calculateM2Px(heightMeters) {
        // if a base offset exists, we should use that measurement instead of the base
        const baseMarker = this.drawableState.baseOffset || this.drawableState.base;
        if (baseMarker == null) {
            return null;
        }
        const baseMarkerXY = baseMarker.getVector();
        const moveVector = new vector_1.Vector(0, -heightMeters);
        const moveDistance = this.drawableState.collection.state
            .getGeometry(this.captureId)
            .movePoint(baseMarkerXY, moveVector);
        return moveDistance;
    }
    /**
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    calculateValue() {
        const baseMarker = this.drawableState.base;
        if (baseMarker == null) {
            return null;
        }
        const adjustOffset = baseMarker.offset || 0;
        const baseMarkerXY = baseMarker.getVector();
        const thisXY = this.getVector();
        const distance = this.drawableState.collection.state
            .getGeometry(this.captureId)
            .calculateDistance(baseMarkerXY, thisXY);
        if (baseMarkerXY.y < thisXY.y) {
            return adjustOffset - distance;
        }
        return adjustOffset + distance;
    }
    verticalDistanceFromLaser() {
        // laser point is assumed to be in the center of the image
        const capture = this.drawableState.capture;
        if (!capture) {
            return null;
        }
        const centerHeight = capture.resolution.height / 2;
        const markerHeight = this.getVector().y;
        const distance = this.drawableState.collection.state
            .getGeometry(this.captureId)
            .calculateWorldDifferenceVector(new vector_1.Vector(0, markerHeight), new vector_1.Vector(0, centerHeight)).y;
        return distance;
    }
    get altitude() {
        var _a, _b, _c;
        return (_b = (_a = this.drawableState.capture) === null || _a === void 0 ? void 0 : _a.target.altitude) !== null && _b !== void 0 ? _b : 0 + ((_c = this.verticalDistanceFromLaser()) !== null && _c !== void 0 ? _c : 0);
    }
    get isDeletable() {
        return this.type === core_api_1.MarkerType.NONE;
    }
    clone(newParentDrawableState) {
        if (!newParentDrawableState.hasDrawable(this._labelId)) {
            this.label.clone(newParentDrawableState);
        }
        return new CollectionModelDrawableMarker(newParentDrawableState, this.id, this.y, this._labelId, this.type, this.insight ? Object.assign({}, this.insight) : undefined, this.pair, this._offset);
    }
    get pairArray() {
        var _a;
        return typeof this._pair === 'string' ? [this._pair] : (_a = this._pair) !== null && _a !== void 0 ? _a : [];
    }
    equals(other) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return (other instanceof CollectionModelDrawableMarker
            && this.type === other.type
            && this.y === other.y
            && this._labelId === other._labelId
            && this._offset === other._offset
            && this.pairArray.join(',') === other.pairArray.join(',')
            && ((_a = this.insight) === null || _a === void 0 ? void 0 : _a.applied) === ((_b = other.insight) === null || _b === void 0 ? void 0 : _b.applied)
            && ((_c = this.insight) === null || _c === void 0 ? void 0 : _c.detection_class) === ((_d = other.insight) === null || _d === void 0 ? void 0 : _d.detection_class)
            && ((_e = this.insight) === null || _e === void 0 ? void 0 : _e.detection_id) === ((_f = other.insight) === null || _f === void 0 ? void 0 : _f.detection_id)
            && ((_g = this.insight) === null || _g === void 0 ? void 0 : _g.detection_version) === ((_h = other.insight) === null || _h === void 0 ? void 0 : _h.detection_version)
            && this._equals(other));
    }
}
exports.CollectionModelDrawableMarker = CollectionModelDrawableMarker;
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelDrawableMarker.prototype, "y", void 0);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableMarker.prototype, "move", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableMarker.prototype, "moveNoBounds", null);
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelDrawableMarker.prototype, "type", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelDrawableMarker.prototype, "insight", void 0);
tslib_1.__decorate([
    mobx_1.computed
], CollectionModelDrawableMarker.prototype, "isInsightMeasurement", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableMarker.prototype, "approveInsightFlag", null);
//# sourceMappingURL=marker.drawable.js.map